<template>
    <div
        v-if="employee"
        class="workforce-inspector-layer"
        :class="{
            'workforce-inspector-layer--behind-modal': behindModal || absenceModalVisible || reserveReturnVisible
        }"
    >
        <button
            type="button"
            class="workforce-inspector-backdrop"
            aria-label="Закрыть сведения о сотруднике"
            @click="$emit('close')"
        ></button>

        <aside class="workforce-inspector" role="dialog" aria-modal="true" :aria-label="employeeName">
            <header class="workforce-inspector__header">
                <div>
                    <div class="workforce-inspector__eyebrow">Сотрудник</div>
                    <h2 class="workforce-inspector__name">{{ employeeName }}</h2>
                </div>
                <button
                    type="button"
                    class="workforce-inspector__close"
                    aria-label="Закрыть"
                    @click="$emit('close')"
                >
                    <i class="fas fa-times" aria-hidden="true"></i>
                </button>
            </header>

            <div
                v-if="loading"
                class="workforce-inspector__loading"
                role="status"
                aria-live="polite"
            >
                <b-spinner small aria-hidden="true"></b-spinner>
                <span>Загружаем данные сотрудника…</span>
            </div>

            <div v-if="canOpenCard" class="workforce-inspector__body">
                <section class="workforce-inspector__summary">
                    <div
                        v-if="inspectorPosition || inspectorSkills.length"
                        class="workforce-inspector__summary-row"
                    >
                        <span>Должность / навыки</span>
                        <div class="workforce-inspector__profession">
                            <strong v-if="inspectorPosition">{{ inspectorPosition }}</strong>
                            <div
                                v-if="inspectorSkills.length"
                                class="workforce-inspector__skills"
                            >
                                <em
                                    v-for="skill in inspectorSkills"
                                    :key="skill.key"
                                    :title="skill.description || skill.name"
                                >
                                    <b>{{ skill.name }}</b>
                                    <span
                                        v-if="skill.description && skill.description !== skill.name"
                                        class="workforce-inspector__skill-description"
                                    >
                                        — {{ skill.description }}
                                    </span>
                                </em>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="employee.citizenship || employee.hiring_form === 'gph'"
                        class="workforce-inspector__summary-row"
                    >
                        <span>{{ employee.citizenship ? 'Гражданство' : 'Форма найма' }}</span>
                        <strong class="workforce-inspector__hiring">
                            <span v-if="employee.citizenship" class="workforce-inspector__hiring-country"
                                  :title="employee.citizenship">{{ employee.citizenship }}</span>
                            <employee-hiring-badge :value="employee.hiring_form" />
                        </strong>
                    </div>
                    <div class="workforce-inspector__summary-row">
                        <span>Приоритет</span>
                        <strong>{{ priorityLabel }}</strong>
                    </div>
                    <div v-if="employee.department_name" class="workforce-inspector__summary-row">
                        <span>Отдел</span>
                        <strong>{{ employee.department_name }}</strong>
                    </div>
                    <div class="workforce-inspector__summary-row">
                        <span>Статус</span>
                        <strong>{{ lifecycleLabel }}</strong>
                        <b-button v-if="employee.permissions && employee.permissions.can_return_from_reserve" class="reserve-return-action" variant="primary" @click="reserveReturnVisible = true"><i class="fas fa-user-check" aria-hidden="true"></i>Вернуть из резерва</b-button>
                    </div>
                </section>

                <employee-journey v-if="employee.card_access && employee.card_access.view && employee.card_access.view.history"
                                  :key="employee.employee_id" :employee-id="employee.employee_id" :date="boardDate" :timesheet-id="timesheetId" sidebar />

                <div v-if="employee.is_absent" class="workforce-inspector__absence">
                    <div class="workforce-inspector__absence-title">
                        <i class="far fa-clock" aria-hidden="true"></i>
                        {{ employee.absence_label || 'Временно отсутствует' }}
                    </div>
                    <div
                        v-if="absencePeriodLabel"
                        class="workforce-inspector__absence-period"
                    >
                        Период: {{ absencePeriodLabel }}
                    </div>
                </div>

                <div
                    v-if="employee.assignment_warning"
                    class="workforce-inspector__notice workforce-inspector__notice--warning"
                >
                    <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                    {{ employee.assignment_warning }}
                </div>

                <section v-if="planSummary" class="workforce-inspector__plan-summary argus-glass-surface" aria-label="План на сегодня">
                    <h3><i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>План на сегодня · {{ formatDate(planSummary.as_of_date) }}</h3>
                    <p v-if="planSummary.current.status === 'unavailable' && planSummary.next.status === 'unavailable'" class="mb-0" role="status">План временно недоступен.</p>
                    <template v-else>
                        <div v-for="entry in planSummaryRows" :key="entry.key" class="workforce-inspector__plan-summary-row">
                            <span class="workforce-inspector__plan-summary-label">{{ entry.label }}</span>
                            <div v-if="entry.status === 'available'" class="workforce-inspector__plan-summary-value">
                                <strong>{{ plannedDestination(entry.item) }}</strong>
                                <small>{{ planPeriodLabel(entry.item) }}</small>
                                <AssignmentAuthor v-if="entry.item.planned_by_name" label="Запланировал"
                                                  :name="entry.item.planned_by_name" :user-id="entry.item.planned_by_user_id" />
                            </div>
                            <span v-else class="workforce-inspector__plan-summary-value">
                                <i v-if="entry.status === 'restricted'" class="fas fa-lock mr-1" aria-hidden="true"></i>{{ planSummaryMessage(entry) }}
                            </span>
                        </div>
                    </template>
                </section>

                <div
                    v-if="displayedAssignments.length"
                    class="workforce-inspector__plans"
                >
                    <div
                        v-for="move in displayedAssignments"
                        :key="move.id"
                        class="workforce-inspector__notice workforce-inspector__notice--planned"
                    >
                        <div class="workforce-inspector__plan-copy">
                            <i class="far fa-calendar-alt" aria-hidden="true"></i>
                            <span class="workforce-inspector__decision-text">
                                <span>{{ plannedMoveLabel(move) }}</span>
                                <AssignmentAuthor v-for="(author, index) in decisionAuthors(move)" :key="index"
                                                  :label="author.label" :name="author.name" :user-id="author.user_id"
                                                  :fallback="author.imported ? 'импорт, автор не указан' : 'автор не указан'" />
                            </span>
                        </div>
                        <div
                            v-if="!move.timesheet_assignment && !readOnly && !isWorkforcePlanTransition(move) && (
                                canMove || employee.can_confirm_planned_move === true
                            )"
                            class="workforce-inspector__plan-actions"
                        >
                            <b-button
                                size="sm"
                                variant="success"
                                :disabled="plannedMovePending"
                                title="Подтвердить прибытие сотрудника"
                                @click="$emit('confirm-planned-move', employeeWithPlannedMove(move))"
                            >
                                <b-spinner v-if="plannedMovePending" small class="mr-1"></b-spinner>
                                <i v-else class="fas fa-check mr-1" aria-hidden="true"></i>
                                Подтвердить прибытие
                            </b-button>
                            <b-button
                                v-if="!readOnly && canMove"
                                size="sm"
                                variant="outline-danger"
                                :disabled="plannedMovePending"
                                @click="$emit('cancel-planned-move', employeeWithPlannedMove(move))"
                            >
                                <i class="fas fa-times mr-1" aria-hidden="true"></i>
                                Отменить
                            </b-button>
                        </div>
                    </div>
                </div>

                <div
                    v-if="employee.scheduled_termination"
                    class="workforce-inspector__termination"
                >
                    <strong>
                        <i class="fas fa-user-clock" aria-hidden="true"></i>
                        Увольнение запланировано на
                        {{ formatDate(employee.scheduled_termination.terminated_on) }}
                    </strong>
                    <span>Указанная дата — последний рабочий день.</span>
                    <small v-if="employee.scheduled_termination.reason">
                        Причина: {{ employee.scheduled_termination.reason }}
                    </small>
                </div>

                <section v-if="canViewContacts" class="workforce-inspector__section">
                    <h3>Контактные данные</h3>
                    <div
                        v-if="telegramLinks.length"
                        class="workforce-inspector__telegram-links"
                    >
                        <a
                            v-for="link in telegramLinks"
                            :key="link.url"
                            :href="link.url"
                            target="_blank"
                            rel="noopener"
                            class="workforce-inspector__telegram-link"
                            @click.stop
                        >
                            <i class="fab fa-telegram-plane" aria-hidden="true"></i>
                            <span>Telegram</span>
                            <strong>{{ link.label }}</strong>
                        </a>
                    </div>
                    <div v-if="nonTelegramContacts.length" class="workforce-inspector__list">
                        <div
                            v-for="(contact, index) in nonTelegramContacts"
                            :key="contact.id || `${contactValue(contact)}-${index}`"
                            class="workforce-inspector__contact"
                        >
                            <i :class="contactIcon(contact)" aria-hidden="true"></i>
                            <div>
                                <div
                                    v-if="contact.label || contact.is_primary"
                                    class="workforce-inspector__item-label"
                                >
                                    {{ contact.label }}
                                    <span v-if="contact.is_primary">
                                        {{ contact.label ? ' · ' : '' }}Основной
                                    </span>
                                </div>
                                <EmployeePhoneMenu v-if="contactType(contact) === 'phone'"
                                                   :value="contactValue(contact)"
                                                   :telegram-username="telegramUsername"
                                                   class="workforce-inspector__phone" />
                                <a
                                    v-else-if="contactHref(contact)"
                                    :href="contactHref(contact)"
                                    :class="{'workforce-inspector__phone': contactType(contact) === 'phone'}"
                                    @click.stop
                                >
                                    {{ contactDisplayValue(contact) }}
                                </a>
                                <span v-else :class="{'workforce-inspector__phone': contactType(contact) === 'phone'}">
                                    {{ contactDisplayValue(contact) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        v-else-if="!telegramLinks.length"
                        class="workforce-inspector__empty"
                    >
                        Контакты не указаны
                    </div>
                </section>

                <section v-if="canViewCharacteristics" class="workforce-inspector__section">
                    <div class="workforce-inspector__section-header">
                        <h3>Характеристики</h3>
                        <b-button
                            v-if="!readOnly && canManageCharacteristics"
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            :disabled="characteristicSaving"
                            @click="toggleCharacteristicEditor"
                        >
                            <i
                                :class="characteristicEditorVisible
                                    ? 'fas fa-chevron-up mr-1'
                                    : 'fas fa-plus mr-1'"
                                aria-hidden="true"
                            ></i>
                            {{ characteristicEditorVisible ? 'Свернуть' : 'Добавить' }}
                        </b-button>
                    </div>
                    <b-form
                        v-if="!readOnly && canManageCharacteristics && characteristicEditorVisible"
                        class="workforce-inspector__characteristic-editor"
                        ref="characteristicEditor"
                        @submit.prevent="addCharacteristic"
                    >
                        <strong>{{ editingCharacteristic ? 'Редактирование характеристики' : 'Новая характеристика' }}</strong>
                        <b-form-group label="Дата характеристики">
                            <b-form-input
                                v-model="characteristicForm.characteristic_date"
                                type="date"
                                :disabled="characteristicSaving"
                            ></b-form-input>
                        </b-form-group>
                        <b-form-group label="Характеристика">
                            <AppTextarea
                                v-model.trim="characteristicForm.content"
                                maxlength="20000"
                                placeholder="Наблюдение кадровика или руководителя"
                                :disabled="characteristicSaving"
                            ></AppTextarea>
                        </b-form-group>
                        <div v-if="characteristicsError" class="workforce-inspector__load-error" role="alert">
                            {{ characteristicsError }}
                        </div>
                        <div class="workforce-inspector__characteristic-editor-actions">
                            <b-button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                :disabled="characteristicSaving"
                                @click="cancelCharacteristicEditor"
                            >
                                Отмена
                            </b-button>
                            <b-button
                                type="submit"
                                size="sm"
                                variant="primary"
                                :disabled="characteristicSaving || !characteristicForm.content"
                            >
                                <b-spinner
                                    v-if="characteristicSaving"
                                    small
                                    class="mr-1"
                                ></b-spinner>
                                Сохранить
                            </b-button>
                        </div>
                    </b-form>
                    <div v-if="visibleCharacteristics.length" class="workforce-inspector__list">
                        <article
                            v-for="(item, index) in visibleCharacteristics"
                            :key="item.id || index"
                            class="workforce-inspector__characteristic"
                            :class="{
                                'workforce-inspector__characteristic--pinned': item.is_pinned,
                                'workforce-inspector__characteristic--editable': canManageCharacteristics,
                                'workforce-inspector__characteristic--own': canManageCharacteristics && item.can_edit
                            }"
                        >
                            <div
                                v-if="['timesheet', 'timesheet_roster'].includes(item.source)"
                                class="workforce-inspector__characteristic-source"
                            >
                                <b-badge variant="primary">{{ item.source === 'timesheet_roster' ? 'Состав табеля' : 'Из табеля' }}</b-badge>
                                <b-badge v-if="item.ktu !== null && item.ktu !== undefined" variant="info">
                                    КТУ {{ item.ktu }}
                                </b-badge>
                                <span
                                    v-if="!timesheetId
                                        && item.source_payload
                                        && item.source_payload.object_name"
                                >
                                    {{ item.source_payload.object_name }}
                                </span>
                            </div>
                            <div class="workforce-inspector__characteristic-text">
                                <p v-if="item.roster_return">Возвращён в табель: {{ rosterReturnMeta(item.roster_return) }}</p>
                                <p
                                    v-for="(paragraph, paragraphIndex) in characteristicParagraphs(item)"
                                    :key="paragraphIndex"
                                >{{ paragraph }}</p>
                            </div>
                            <div
                                v-if="characteristicMeta(item) || item.created_by"
                                class="workforce-inspector__characteristic-meta"
                            >
                                {{ characteristicMeta(item) }}
                                <template v-if="item.created_by">
                                    <span v-if="characteristicMeta(item)"> · </span>
                                    <AssignmentAuthor label="Автор" :name="item.author_name || item.author" :user-id="item.created_by" />
                                </template>
                            </div>
                            <b-button
                                v-if="!readOnly && canManageCharacteristics && item.can_edit"
                                v-b-tooltip.hover.top type="button"
                                size="sm" variant="link" class="workforce-inspector__characteristic-edit"
                                :class="{'workforce-inspector__characteristic-edit--new': String(item.id) === String(newCharacteristicId)}"
                                @animationend.native="newCharacteristicId = null"
                                title="Редактировать характеристику" aria-label="Редактировать характеристику"
                                :disabled="characteristicSaving"
                                @click="editCharacteristic(item)"
                            ><i class="fas fa-pencil-alt" aria-hidden="true"></i></b-button>
                            <b-button
                                v-if="!readOnly && canManageCharacteristics"
                                v-b-tooltip.hover.top
                                type="button"
                                size="sm"
                                variant="link"
                                class="workforce-inspector__characteristic-pin"
                                :title="item.is_pinned ? 'Открепить' : 'Закрепить'"
                                :aria-label="item.is_pinned ? 'Открепить характеристику' : 'Закрепить характеристику'"
                                :disabled="Boolean(characteristicActionId)"
                                @click="togglePinnedCharacteristic(item)"
                            >
                                <b-spinner
                                    v-if="String(characteristicActionId) === String(item.id)"
                                    small
                                ></b-spinner>
                                <i v-else class="fas fa-thumbtack" aria-hidden="true"></i>
                            </b-button>
                        </article>
                        <button
                            v-if="canToggleCharacteristics"
                            type="button"
                            class="workforce-inspector__more"
                            :disabled="characteristicsLoading"
                            @click="toggleCharacteristics"
                        >
                            <b-spinner
                                v-if="characteristicsLoading"
                                small
                                class="mr-1"
                            ></b-spinner>
                            <i
                                v-else
                                :class="
                                    characteristicsExpanded
                                        ? 'fas fa-chevron-up'
                                        : 'fas fa-chevron-down'
                                "
                                aria-hidden="true"
                            ></i>
                            {{ characteristicsToggleLabel }}
                        </button>
                    </div>
                    <div v-else class="workforce-inspector__empty">Характеристик пока нет</div>
                    <div
                        v-if="characteristicsError && !characteristicEditorVisible"
                        class="workforce-inspector__load-error"
                    >
                        {{ characteristicsError }}
                    </div>
                </section>
                <slot name="extra"></slot>
            </div>

            <div v-else class="workforce-inspector__body">
                <div v-for="assignment in assignmentNotices" :key="assignment.id"
                     class="workforce-inspector__notice workforce-inspector__notice--planned">
                    <div class="workforce-inspector__plan-copy">
                        <i class="far fa-calendar-alt" aria-hidden="true"></i>
                        <span class="workforce-inspector__decision-text">
                            <span>{{ assignment.label }}</span>
                            <AssignmentAuthor v-for="(author, index) in decisionAuthors(assignment)" :key="index"
                                              :label="author.label" :name="author.name" :user-id="author.user_id"
                                              :fallback="author.imported ? 'импорт, автор не указан' : 'автор не указан'" />
                        </span>
                    </div>
                </div>
                <div
                    v-if="!loading"
                    class="workforce-inspector__notice workforce-inspector__notice--restricted"
                    role="status"
                >
                    <i class="fas fa-lock" aria-hidden="true"></i>
                    <div>
                        <strong>Доступ ограничен</strong>
                        <span>
                            {{ accessMessage || 'Подробные сведения об этом сотруднике вам недоступны.' }}
                        </span>
                    </div>
                </div>
                <slot name="restricted"></slot>
            </div>

            <slot name="status"></slot>
            <footer v-if="canOpenCard" class="workforce-inspector__footer">
                <b-button
                    v-if="canOpenPlan"
                    size="sm"
                    variant="outline-primary"
                    :to="employeePlanRoute"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Открыть план сотрудника в новой вкладке"
                >
                    <i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>
                    Открыть в плане
                </b-button>
                <b-button
                    v-if="canCreateAbsence"
                    size="sm"
                    variant="outline-primary"
                    @click="openAbsenceModal"
                >
                    <i class="far fa-clock mr-1" aria-hidden="true"></i>
                    Добавить отсутствие
                </b-button>
                <b-button
                    v-if="!readOnly && canMove"
                    size="sm"
                    variant="primary"
                    @click="$emit('move', employee)"
                >
                    <i class="fas fa-map-marked-alt mr-1" aria-hidden="true"></i>
                    Назначения
                </b-button>
                <b-button
                    v-if="employeeCardId && employee.can_open_card === true"
                    size="sm"
                    variant="outline-secondary"
                    :href="employeeCardHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Открыть карточку в новой вкладке"
                >
                    <i class="fas fa-external-link-alt mr-1" aria-hidden="true"></i>
                    Открыть карточку
                </b-button>
            </footer>
        </aside>

        <b-modal
            v-model="absenceModalVisible"
            title="Временное отсутствие"
            centered
            scrollable
            hide-footer
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="absenceSaving"
            :no-close-on-esc="absenceSaving"
        >
            <b-alert v-if="absenceError" show variant="danger">
                {{ absenceError }}
            </b-alert>
            <b-form @submit.prevent="createAbsence">
                <b-form-group label="Обозначение">
                    <b-form-select
                        v-model="absenceForm.absence_type"
                        :options="absenceTypeOptions"
                        :disabled="absenceSaving"
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Дата начала">
                    <b-form-input
                        v-model="absenceForm.starts_on"
                        type="date"
                        :disabled="absenceSaving"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Плановая дата окончания (необязательно)">
                    <b-form-input
                        v-model="absenceForm.planned_ends_on"
                        type="date"
                        :min="absenceForm.starts_on"
                        :disabled="absenceSaving"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Примечание">
                    <AppTextarea
                        v-model.trim="absenceForm.note"
                        :disabled="absenceSaving"
                    ></AppTextarea>
                </b-form-group>
                <b-alert show variant="secondary" class="py-2">
                    Плановая дата не завершает отсутствие автоматически: возвращение нужно
                    подтвердить вручную.
                </b-alert>
                <div class="d-flex justify-content-end">
                    <b-button
                        type="button"
                        variant="outline-secondary"
                        class="mr-2"
                        :disabled="absenceSaving"
                        @click="absenceModalVisible = false"
                    >
                        Отмена
                    </b-button>
                    <b-button
                        type="submit"
                        variant="primary"
                        :disabled="absenceSaving || !absenceForm.absence_type || !absenceForm.starts_on"
                    >
                        <b-spinner v-if="absenceSaving" small class="mr-1"></b-spinner>
                        Сохранить
                    </b-button>
                </div>
            </b-form>
        </b-modal>
        <employee-reserve-return :visible="reserveReturnVisible" :employee-id="employeeCardId" :context="requestParams()" @close="reserveReturnVisible = false" @returned="$emit('reserve-returned')" />
    </div>
</template>

<script>
import EmployeeReserveReturn from './EmployeeReserveReturn.vue';
import EmployeeHiringBadge from './EmployeeHiringBadge.vue';
import EmployeeJourney from './EmployeeJourney.vue';
import {employeePlanRoute} from '@/utils/employeeWorkforcePlanNavigation';
import {createEmployeeInspectorHistory} from '@/utils/employeeInspectorHistory';
import {formatPhone, phoneHref} from '@/utils/phone';
import EmployeePhoneMenu from '@/components/EmployeePhoneMenu.vue';
import {employeeTelegramLinks, telegramHref, employeeTelegramUsername} from '@/utils/employeeTelegram';
import api from '@/utils/api';
import {SOURCE_SKILL_DESCRIPTIONS} from '@/utils/employeeSkills';
import {employeeAbsencePeriodLabel} from './employeeAbsencePresentation';
import {employeeCharacteristicParagraphs} from './employeeCharacteristicPresentation';

function currentLocalDate() {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function emptyCharacteristicForm() {
    return {
        characteristic_date: currentLocalDate(),
        content: ''
    };
}

function emptyAbsenceForm() {
    return {
        absence_type: '',
        starts_on: currentLocalDate(),
        planned_ends_on: '',
        note: ''
    };
}

import AssignmentAuthor from '@/components/AssignmentAuthor.vue';

export default {
    components: {EmployeeReserveReturn, EmployeeHiringBadge, EmployeeJourney, EmployeePhoneMenu, AssignmentAuthor},
    name: 'EmployeeInspector',
    props: {
        assignmentNotices: {type: Array, default: () => []},
        manageHistory: {type: Boolean, default: true},
        readOnly: {type: Boolean, default: false},
        canViewPlan: {type: Boolean, default: false},
        cardSource: {type: String, default: ''},
        employee: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        canMove: {
            type: Boolean,
            default: false
        },
        canManageCharacteristics: {
            type: Boolean,
            default: false
        },
        canManageAbsences: {
            type: Boolean,
            default: false
        },
        plannedMovePending: {
            type: Boolean,
            default: false
        },
        behindModal: {
            type: Boolean,
            default: false
        },
        boardDate: {
            type: String,
            default: ''
        },
        timesheetId: {
            type: [Number, String],
            default: null
        },
        accessMessage: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            reserveReturnVisible: false,
            characteristicsExpanded: false,
            fullCharacteristics: null,
            characteristicsLoading: false,
            characteristicsError: '',
            characteristicsRequestId: 0,
            characteristicActionId: null,
            pinnedCharacteristicId: null,
            characteristicEditorVisible: false,
            editingCharacteristic: null,
            newCharacteristicId: null,
            characteristicSaving: false,
            characteristicForm: emptyCharacteristicForm(),
            absenceModalVisible: false,
            absenceSaving: false,
            absenceError: '',
            absenceForm: emptyAbsenceForm(),
            absenceTypeOptions: [
                {value: '', text: 'Выберите обозначение'},
                {value: 'labor_leave', text: 'О — трудовой отпуск'},
                {value: 'unpaid_leave', text: 'А — отпуск за свой счёт'},
                {value: 'sick_leave', text: 'Б — больничный'},
                {value: 'state_duties', text: 'Г — гособязанности'},
                {value: 'absence', text: 'П — прогул'}
            ]
        };
    },
    computed: {
        telegramUsername() { return employeeTelegramUsername(this.employee) || ''; },
        employeeName() {
            if (!this.employee) {
                return '';
            }
            return this.employee.display_name ||
                this.employee.full_name ||
                this.employee.name ||
                `Сотрудник #${this.employee.employee_id || this.employee.id || ''}`;
        },
        employeeCardId() {
            if (!this.employee) {
                return null;
            }
            return this.employee.employee_id || this.employee.id || null;
        },
        employeeCardHref() {
            if (!this.employeeCardId || !this.canOpenCard) return null;
            const query = {
                date: this.boardDate,
                from: this.cardSource || (this.timesheetId ? 'timesheet' : 'workforce')
            };
            if (this.timesheetId) query.timesheet_id = String(this.timesheetId);
            return this.$router.resolve({
                path: `/employees/${this.employeeCardId}`,
                query
            }).href;
        },
        canOpenPlan() {
            return Boolean(!this.loading && this.canOpenCard && this.employeeCardId
                && (this.canViewPlan || this.employee.permissions && this.employee.permissions.can_view_plan === true));
        },
        employeePlanRoute() {
            return this.canOpenPlan ? employeePlanRoute(this.employeeCardId,
                this.employee.plan_summary && this.employee.plan_summary.as_of_date || currentLocalDate(), []) : null;
        },
        priorityLabel() {
            if (!this.employee ||
                this.employee.priority === null ||
                this.employee.priority === undefined ||
                this.employee.priority === '') {
                return 'Не назначен';
            }
            return String(this.employee.priority);
        },
        inspectorPosition() {
            if (!this.employee) return '';
            const currentEmployment = this.employee.as_of
                && this.employee.as_of.current_employment;
            const latestEmployment = this.employee.latest_employment_period;
            return this.employee.position
                || this.employee.operational_position
                || currentEmployment && currentEmployment.official_position
                || latestEmployment && latestEmployment.official_position
                || '';
        },
        inspectorSkills() {
            const skills = this.employee && Array.isArray(this.employee.skills)
                ? this.employee.skills
                : [];
            const details = new Map(
                (this.employee && Array.isArray(this.employee.skill_details)
                    ? this.employee.skill_details
                    : []
                ).map(skill => [
                    String(skill && (skill.name || skill.code) || '')
                        .trim()
                        .toLocaleUpperCase('ru-RU'),
                    skill && skill.description || null
                ])
            );
            const seen = new Set();
            return skills.reduce((result, skill, index) => {
                const name = String(
                    typeof skill === 'string'
                        ? skill
                        : skill && (skill.name || skill.code || skill.label) || ''
                ).trim();
                if (!name) return result;
                const normalized = name.toLocaleUpperCase('ru-RU');
                if (seen.has(normalized)) return result;
                seen.add(normalized);
                result.push({
                    key: skill && skill.id || `${normalized}:${index}`,
                    name,
                    description: typeof skill === 'object' && skill
                        ? skill.description
                            || details.get(normalized)
                            || SOURCE_SKILL_DESCRIPTIONS[normalized]
                            || null
                        : details.get(normalized)
                            || SOURCE_SKILL_DESCRIPTIONS[normalized]
                            || null
                });
                return result;
            }, []);
        },
        lifecycleLabel() {
            const status = this.employee && this.employee.lifecycle_status;
            return {
                active: 'Действующий',
                reserve: 'Резерв',
                unverified: 'Непроверенный'
            }[status] || 'Не указан';
        },
        canOpenCard() {
            return Boolean(
                this.employee
                && (
                    this.employee.can_open_card === true
                    || this.employee.card_access
                        && this.employee.card_access.can_open === true
                )
            );
        },
        canViewContacts() {
            return Boolean(
                this.employee
                && this.employee.card_access
                && this.employee.card_access.view
                && this.employee.card_access.view.contacts === true
            );
        },
        canViewCharacteristics() {
            return Boolean(
                this.employee
                && this.employee.card_access
                && this.employee.card_access.view
                && this.employee.card_access.view.characteristics === true
            );
        },
        displayedAssignments() {
            return this.assignmentNotices.concat(this.activePlannedMoves);
        },
        planSummary() {
            if (this.loading || !this.canOpenCard || !this.employee.card_access
                || !this.employee.card_access.view || this.employee.card_access.view.distribution !== true) return null;
            return this.employee.plan_summary || {
                as_of_date: currentLocalDate(), current: {status: 'unavailable'}, next: {status: 'unavailable'}
            };
        },
        planSummaryRows() {
            return this.planSummary ? ['current', 'next'].map(key => ({...this.planSummary[key], key,
                label: key === 'current' ? 'Сейчас по плану' : 'Далее'})) : [];
        },
        activePlannedMoves() {
            const legacyMoves = this.employee && Array.isArray(this.employee.planned_moves)
                ? this.employee.planned_moves
                : (this.employee && this.employee.planned_move
                    ? [this.employee.planned_move]
                    : []);
            return legacyMoves
                .filter(move => move && move.status === 'planned' && !move.workforce_plan_transition)
                .sort((left, right) => String(left.planned_for || '')
                    .localeCompare(String(right.planned_for || '')));
        },
        contacts() {
            return this.employee && Array.isArray(this.employee.contacts)
                ? this.employee.contacts
                : [];
        },
        nonTelegramContacts() {
            return this.contacts.filter(contact => (
                this.contactType(contact) !== 'telegram'
            ));
        },
        telegramLinks() {
            return employeeTelegramLinks(this.employee);
        },
        absencePeriodLabel() {
            return employeeAbsencePeriodLabel(
                this.employee && this.employee.absence
            );
        },
        canCreateAbsence() {
            return Boolean(
                !this.readOnly && this.canManageAbsences
                && this.employee
                && this.employee.lifecycle_status !== 'reserve'
                && !this.employee.is_absent
            );
        },
        characteristics() {
            return this.employee && Array.isArray(this.employee.characteristics)
                ? this.employee.characteristics
                : [];
        },
        characteristicsTotal() {
            const total = Number(
                this.employee && this.employee.characteristics_total
            );
            return Number.isInteger(total) && total >= this.characteristics.length
                ? total
                : this.characteristics.length;
        },
        visibleCharacteristics() {
            const characteristics = this.characteristicsExpanded
                && Array.isArray(this.fullCharacteristics)
                ? this.fullCharacteristics
                : this.characteristics;
            return this.decorateCharacteristics(characteristics);
        },
        hiddenCharacteristicsCount() {
            return Math.max(0, this.characteristicsTotal - this.characteristics.length);
        },
        canToggleCharacteristics() {
            return this.characteristicsExpanded || this.hiddenCharacteristicsCount > 0;
        },
        characteristicsToggleLabel() {
            if (this.characteristicsLoading) return 'Загружаю…';
            return this.characteristicsExpanded
                ? 'Свернуть'
                : `Ещё ${this.hiddenCharacteristicsCount}`;
        }
    },
    mounted() {
        if (!this.manageHistory) return;
        this.inspectorHistory = createEmployeeInspectorHistory(window, () => this.$emit('close'));
        if (this.employee) this.inspectorHistory.open();
    },
    beforeDestroy() {
        if (this.inspectorHistory) this.inspectorHistory.destroy();
    },
    watch: {
        employee: {
            immediate: true,
            handler(employee, previousEmployee) {
                if (this.inspectorHistory) {
                    if (employee) this.inspectorHistory.open();
                    else this.inspectorHistory.close();
                }
                this.characteristicsRequestId += 1;
                this.characteristicsExpanded = false;
                this.fullCharacteristics = null;
                this.characteristicsLoading = false;
                this.characteristicsError = '';
                this.characteristicActionId = null;
                this.pinnedCharacteristicId = Number(
                    employee && employee.pinned_characteristic_id
                ) || null;
                const employeeId = employee && (
                    employee.employee_id || employee.id
                );
                const previousEmployeeId = previousEmployee && (
                    previousEmployee.employee_id || previousEmployee.id
                );
                if (String(employeeId || '') !== String(previousEmployeeId || '')) {
                    this.characteristicEditorVisible = false;
                    this.editingCharacteristic = null;
                    this.newCharacteristicId = null;
                    this.characteristicSaving = false;
                    this.characteristicForm = emptyCharacteristicForm();
                    this.reserveReturnVisible = false;
                    this.absenceModalVisible = false;
                    this.absenceSaving = false;
                    this.absenceError = '';
                    this.absenceForm = emptyAbsenceForm();
                }
            }
        }
    },
    methods: {
        requestParams() {
            const params = {};
            if (this.boardDate) params.date = this.boardDate;
            const timesheetId = Number(this.timesheetId);
            if (Number.isInteger(timesheetId) && timesheetId > 0) {
                params.timesheet_id = timesheetId;
            }
            return params;
        },
        openAbsenceModal() {
            if (!this.canCreateAbsence) return;
            this.absenceForm = emptyAbsenceForm();
            this.absenceError = '';
            this.absenceModalVisible = true;
        },
        async createAbsence() {
            const employeeId = this.employeeCardId;
            if (
                !employeeId
                || !this.canCreateAbsence
                || this.absenceSaving
                || !this.absenceForm.absence_type
                || !this.absenceForm.starts_on
            ) {
                return;
            }
            this.absenceSaving = true;
            this.absenceError = '';
            try {
                const response = await api.post(
                    `/api/employees/${encodeURIComponent(employeeId)}/absences`,
                    {
                        absence_type: this.absenceForm.absence_type,
                        starts_on: this.absenceForm.starts_on,
                        planned_ends_on: this.absenceForm.planned_ends_on || null,
                        note: this.absenceForm.note || null
                    },
                    {
                        params: this.requestParams(),
                        ignoreModal: true
                    }
                );
                this.absenceModalVisible = false;
                this.$emit('absence-added', response && response.data || null);
                this.$bvToast.toast('Временное отсутствие добавлено', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 2500
                });
            } catch (error) {
                this.absenceError = this.errorText(
                    error,
                    'Не удалось сохранить временное отсутствие.'
                );
            } finally {
                this.absenceSaving = false;
            }
        },
        planSummaryMessage(entry) {
            if (entry.status === 'restricted') return 'Скрыто правами доступа';
            if (entry.status !== 'empty') return 'План временно недоступен';
            return entry.key === 'current' ? 'На сегодня план не заполнен' : 'Следующий переход не запланирован';
        },
        planPeriodLabel(item) {
            const end = new Date(`${item.ends_on}T00:00:00Z`);
            end.setUTCDate(end.getUTCDate() - 1);
            return `${this.formatDate(item.starts_on)} — ${this.formatDate(end.toISOString().slice(0, 10))}`;
        },
        plannedDestination(move) {
            const namedTarget = move.target_name
                ? (move.target_code
                    ? `${move.target_code} — ${move.target_name}`
                    : move.target_name)
                : null;
            return namedTarget || {
                home: 'отдых',
                requires_distribution: 'не распределён',
                loupe: 'выбыл из планирования',
                planned_object: 'плановый объект',
                object_group: 'группа объектов',
                object: 'объект'
            }[move.target_type] || 'новое место';
        },
        plannedMoveLabel(move) {
            if (move.timesheet_assignment) return move.label;
            const destination = this.plannedDestination(move);
            const label = move.workforce_plan_transition === true
                ? `С ${this.formatDate(move.planned_for)} по Плану Аргуса: ${destination}`
                : `Запланировано на ${this.formatDate(move.planned_for)}: ${destination}`;
            return label;
        },
        decisionAuthors(move) {
            return move.timesheet_assignment ? move.authors || []
                : [{label: 'Запланировал', name: move.planned_by_name, user_id: move.planned_by_user_id}];
        },
        isWorkforcePlanTransition(move) {
            return Boolean(move && move.workforce_plan_transition === true);
        },
        employeeWithPlannedMove(move) {
            return Object.assign({}, this.employee, {
                planned_move: move,
                planned_moves: [move]
            });
        },
        formatDate(value) {
            const parts = String(value || '').split('-');
            return parts.length === 3
                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                : String(value || '');
        },
        contactType(contact) {
            return String(contact.contact_type || contact.type || '').toLowerCase();
        },
        contactValue(contact) {
            return contact.value || contact.contact || contact.text || '';
        },
        contactDisplayValue(contact) {
            const value = this.contactValue(contact);
            return this.contactType(contact) === 'phone' ? formatPhone(value) : value;
        },
        contactIcon(contact) {
            const type = this.contactType(contact);
            if (type === 'phone') {
                return 'fas fa-phone';
            }
            if (type === 'email') {
                return 'fas fa-envelope';
            }
            if (type === 'telegram') {
                return 'fab fa-telegram-plane';
            }
            return 'fas fa-address-card';
        },
        contactHref(contact) {
            const value = String(this.contactValue(contact) || '').trim();
            const type = this.contactType(contact);
            if (!value) {
                return null;
            }
            if (type === 'email') {
                return `mailto:${value}`;
            }
            if (type === 'phone') {
                return phoneHref(value);
            }
            if (type === 'telegram') {
                return this.telegramHref(contact.url || value);
            }
            return null;
        },
        telegramHref,
        toggleCharacteristicEditor() {
            if (this.readOnly) return;
            const open = !this.characteristicEditorVisible || Boolean(this.editingCharacteristic);
            this.cancelCharacteristicEditor();
            this.characteristicEditorVisible = open;
            this.characteristicsError = '';
        },
        editCharacteristic(item) {
            if (this.readOnly) return;
            this.newCharacteristicId = null;
            this.editingCharacteristic = {id: item.id, revision: item.revision};
            this.characteristicForm = {characteristic_date: item.characteristic_date, content: item.content};
            this.characteristicEditorVisible = true;
            this.characteristicsError = '';
            this.$nextTick(() => {
                const editor = this.$refs.characteristicEditor;
                if (editor) (editor.$el || editor).scrollIntoView({block: 'nearest', behavior: 'smooth'});
            });
        },
        cancelCharacteristicEditor() {
            this.characteristicEditorVisible = false;
            this.editingCharacteristic = null;
            this.characteristicForm = emptyCharacteristicForm();
            this.characteristicsError = '';
        },
        async addCharacteristic() {
            if (this.readOnly) return;
            const employeeId = this.employeeCardId;
            const content = String(this.characteristicForm.content || '').trim();
            if (!employeeId || !content || this.characteristicSaving) return;
            this.characteristicSaving = true;
            this.characteristicsError = '';
            try {
                const editing = this.editingCharacteristic;
                const response = await api[editing ? 'put' : 'post'](
                    editing ? `/api/employees/characteristics/${editing.id}`
                        : `/api/employees/${encodeURIComponent(employeeId)}/characteristics`,
                    {
                        characteristic_date:
                            this.characteristicForm.characteristic_date
                            || currentLocalDate(),
                        content,
                        expected_revision: editing ? editing.revision : undefined
                    },
                    {
                        params: this.requestParams(),
                        ignoreModal: true
                    }
                );
                if (String(this.employeeCardId) !== String(employeeId)) return;
                const created = response && response.data;
                if (!editing && created && created.id) this.newCharacteristicId = created.id;
                if (created && created.id) {
                    const current = Array.isArray(this.fullCharacteristics)
                        ? this.fullCharacteristics
                        : this.characteristics;
                    this.fullCharacteristics = [created].concat(
                        current.filter(item => String(item && item.id) !== String(created.id))
                    );
                    this.characteristicsExpanded = true;
                }
                this.characteristicForm = emptyCharacteristicForm();
                this.characteristicEditorVisible = false;
                this.editingCharacteristic = null;
                this.$emit(editing ? 'characteristic-updated' : 'characteristic-added', created || null);
                this.$bvToast.toast(editing ? 'Характеристика изменена' : 'Характеристика добавлена', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 2500
                });
            } catch (error) {
                this.characteristicsError = this.errorText(
                    error,
                    'Не удалось сохранить характеристику.'
                );
            } finally {
                this.characteristicSaving = false;
            }
        },
        characteristicText(item) {
            if (typeof item === 'string') {
                return item;
            }
            return item.content ||
                item.text ||
                item.body ||
                item.value ||
                item.description ||
                'Без текста';
        },
        characteristicParagraphs(item) {
            return employeeCharacteristicParagraphs(this.characteristicText(item));
        },
        rosterReturnMeta(item) { return `${item.author_name || 'Пользователь №' + item.author_id}, ${new Date(item.at).toLocaleString('ru-RU')}`; },
        characteristicMeta(item) {
            if (!item || typeof item === 'string') {
                return '';
            }
            const values = [];
            const rawDate = (item.source === 'timesheet_roster' && (item.created_at || item.createdAt)) || item.characteristic_date ||
                item.date ||
                item.created_at ||
                item.effective_date;
            if (rawDate) {
                const parsed = new Date(rawDate);
                values.push(Number.isNaN(parsed.getTime())
                    ? String(rawDate)
                    : item.source === 'timesheet_roster' ? parsed.toLocaleString('ru-RU') : parsed.toLocaleDateString('ru-RU'));
            }
            const author = item.author_name || item.author;
            if (author && !item.created_by) {
                values.push(`Автор: ${author}`);
            } else if (!author && !item.created_by && item.source) {
                values.push('Автор не сохранён');
            }
            return values.join(' · ');
        },
        decorateCharacteristics(items) {
            const pinnedId = Number(this.pinnedCharacteristicId);
            return (Array.isArray(items) ? items : []).map(item => {
                if (!item || typeof item === 'string') return item;
                return Object.assign({}, item, {
                    is_pinned: pinnedId > 0 && Number(item.id) === pinnedId
                });
            }).sort((left, right) => {
                const leftPinned = Boolean(left && left.is_pinned);
                const rightPinned = Boolean(right && right.is_pinned);
                if (leftPinned !== rightPinned) return leftPinned ? -1 : 1;
                const leftDate = left && (
                    left.characteristic_date || left.date || left.created_at
                ) || '';
                const rightDate = right && (
                    right.characteristic_date || right.date || right.created_at
                ) || '';
                const dateComparison = String(rightDate).localeCompare(String(leftDate));
                return dateComparison || Number(right && right.id || 0) -
                    Number(left && left.id || 0);
            });
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            if (typeof data === 'string' && data) return data;
            return fallback;
        },
        async togglePinnedCharacteristic(item) {
            if (this.readOnly) return;
            if (
                !this.canManageCharacteristics
                || this.characteristicActionId
                || !item
                || !item.id
                || !this.employeeCardId
            ) {
                return;
            }
            const itemId = Number(item.id);
            const pinnedId = Number(this.pinnedCharacteristicId);
            const isPinned = pinnedId > 0 && itemId === pinnedId;
            if (!isPinned && pinnedId > 0) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Уже закреплена другая характеристика. После подтверждения она будет заменена выбранной.',
                    {
                        title: 'Замена закреплённой характеристики',
                        okTitle: 'Заменить',
                        cancelTitle: 'Отмена',
                        okVariant: 'primary',
                        centered: true
                    }
                );
                if (!confirmed) return;
            }
            this.characteristicActionId = item.id;
            this.characteristicsError = '';
            try {
                const nextPinnedId = isPinned ? null : item.id;
                await api.put(
                    `/api/employees/${encodeURIComponent(this.employeeCardId)}/pinned-characteristic`,
                    {characteristic_id: nextPinnedId},
                    {
                        params: this.requestParams(),
                        ignoreModal: true
                    }
                );
                this.pinnedCharacteristicId = Number(nextPinnedId) || null;
                this.$emit('characteristic-pin-changed', {
                    employee_id: this.employeeCardId,
                    pinned_characteristic_id: this.pinnedCharacteristicId,
                    characteristic: Object.assign({}, item)
                });
                this.$bvToast.toast(
                    isPinned
                        ? 'Характеристика откреплена'
                        : 'Характеристика закреплена',
                    {
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 2500
                    }
                );
            } catch (error) {
                this.characteristicsError = this.errorText(
                    error,
                    'Не удалось изменить закреплённую характеристику.'
                );
            } finally {
                this.characteristicActionId = null;
            }
        },
        async toggleCharacteristics() {
            if (this.characteristicsExpanded) {
                this.characteristicsExpanded = false;
                return;
            }
            if (Array.isArray(this.fullCharacteristics)) {
                this.characteristicsExpanded = true;
                return;
            }
            const employeeId = this.employeeCardId;
            if (!employeeId || this.characteristicsLoading) return;
            const requestId = ++this.characteristicsRequestId;
            this.characteristicsLoading = true;
            this.characteristicsError = '';
            try {
                const response = await api.get(
                    `/api/employees/${encodeURIComponent(employeeId)}/characteristics`,
                    {params: this.requestParams()}
                );
                if (
                    requestId !== this.characteristicsRequestId
                    || String(this.employeeCardId) !== String(employeeId)
                ) {
                    return;
                }
                this.fullCharacteristics = Array.isArray(response.data.characteristics)
                    ? response.data.characteristics
                    : [];
                this.characteristicsExpanded = true;
            } catch (error) {
                if (requestId !== this.characteristicsRequestId) return;
                this.characteristicsError = error.response
                    && error.response.data
                    && error.response.data.msg
                    || 'Не удалось загрузить остальные характеристики.';
            } finally {
                if (requestId === this.characteristicsRequestId) {
                    this.characteristicsLoading = false;
                }
            }
        }
    }
};
</script>

<style src="./workforce-viewport-modal.css"></style>
<style src="./reserve-return-action.css"></style>

<style scoped>
.workforce-inspector-layer {
    position: fixed;
    z-index: 1090;
    inset: 0;
    overflow: hidden;
}

.workforce-inspector-layer--behind-modal {
    z-index: 1030;
}

.workforce-inspector-backdrop {
    position: absolute;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: rgba(24, 34, 45, 0.28);
    cursor: default;
}

.workforce-inspector {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    width: min(430px, 92vw);
    height: 100%;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
    background: #fff;
    box-shadow: -10px 0 30px rgba(24, 34, 45, 0.2);
    container-name: workforce-inspector;
    container-type: inline-size;
}

.workforce-inspector__header {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 22px 17px;
    border-bottom: 1px solid #e3e8ee;
}

.workforce-inspector__loading {
    display: flex;
    min-height: 40px;
    padding: 8px 22px;
    align-items: center;
    gap: 9px;
    flex: 0 0 auto;
    border-bottom: 1px solid #b8d8f5;
    background: #edf6ff;
    color: #2468a8;
    font-size: 0.78rem;
    font-weight: 700;
}

.workforce-inspector__eyebrow {
    margin-bottom: 4px;
    color: #7a8794;
    font-size: 0.71rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.workforce-inspector__name {
    margin: 0;
    color: #253545;
    font-size: 1.28rem;
    line-height: 1.25;
}

.workforce-inspector__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #eef2f6;
    color: #506174;
}

.workforce-inspector__body {
    /* Keep the current reading position when characteristics are expanded below it. */
    overflow-anchor: none;
    flex: 1 1 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 18px 22px 26px;
    overscroll-behavior-y: contain;
    scrollbar-gutter: stable;
}

.workforce-inspector__summary {
    overflow: hidden;
    border: 1px solid #e1e7ed;
    border-radius: 10px;
}

.workforce-inspector__summary-row {
    display: grid;
    grid-template-columns: minmax(90px, 0.8fr) minmax(120px, 1.2fr);
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid #edf0f3;
    font-size: 0.82rem;
}

.workforce-inspector__summary-row:last-child {
    border-bottom: 0;
}

.workforce-inspector__summary-row span {
    color: #7b8794;
}

.workforce-inspector__summary-row strong {
    color: #344556;
    font-weight: 600;
}

.workforce-inspector__hiring {
    display: flex;
    min-width: 0;
    align-items: baseline;
}

.workforce-inspector__hiring .workforce-inspector__hiring-country {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: inherit;
}

.workforce-inspector__profession {
    display: flex;
    min-width: 0;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.workforce-inspector__skills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.workforce-inspector__skills em {
    display: inline-flex;
    max-width: 100%;
    padding: 2px 6px;
    align-items: baseline;
    gap: 3px;
    border-radius: 999px;
    background: #e8f1fb;
    color: #245a8d;
    font-size: 0.68rem;
    font-style: normal;
    font-weight: 800;
    line-height: 1.25;
}

.workforce-inspector__skills em b { font: inherit; }

.workforce-inspector__skill-description {
    min-width: 0;
    overflow: hidden;
    font-weight: 600;
    opacity: 0.82;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-inspector__absence {
    margin-top: 13px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff4dc;
    color: #8f5908;
    font-size: 0.82rem;
    font-weight: 600;
}

.workforce-inspector__absence-period {
    margin-top: 4px;
    padding-left: 19px;
    color: #74531d;
    font-size: 0.75rem;
    font-weight: 400;
}

.workforce-inspector__notice {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
}

.workforce-inspector__notice i {
    margin-right: 4px;
}

.workforce-inspector__notice--warning {
    background: #fff4dc;
    color: #8f5908;
}

.workforce-inspector__notice--restricted {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 0;
    border: 1px solid #d8e0e8;
    background: #f4f7fa;
    color: #40556a;
}

.workforce-inspector__notice--restricted i {
    margin-top: 2px;
}

.workforce-inspector__notice--restricted strong,
.workforce-inspector__notice--restricted span {
    display: block;
}

.workforce-inspector__notice--restricted span {
    margin-top: 3px;
    font-weight: 400;
}

.workforce-inspector__notice--planned {
    padding: 0;
    overflow: hidden;
    border: 1px solid #c7dcef;
    background: #eaf3fd;
    color: #265f96;
}

.workforce-inspector__plans {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
}

.workforce-inspector__plan-summary {
    margin-top: 12px;
    padding: 12px;
    border-radius: 10px;
    color: #344b61;
    font-size: 0.8rem;
}

.workforce-inspector__plan-summary h3 {
    margin: 0 0 10px;
    color: #45647e;
    font-size: 0.82rem;
    font-weight: 700;
}

.workforce-inspector__plan-summary-row {
    display: grid;
    grid-template-columns: 105px minmax(0, 1fr);
    gap: 8px;
    align-items: baseline;
}

.workforce-inspector__plan-summary-row + .workforce-inspector__plan-summary-row {
    margin-top: 9px;
    padding-top: 9px;
    border-top: 1px solid #e2eaf0;
}

.workforce-inspector__plan-summary-label { color: #617487; }
.workforce-inspector__plan-summary-value { min-width: 0; overflow-wrap: anywhere; }
.workforce-inspector__plan-summary-value > strong,
.workforce-inspector__plan-summary-value > small,
.workforce-inspector__plan-summary-value > .assignment-author { display: block; }
.workforce-inspector__plan-summary-value > small,
.workforce-inspector__plan-summary-value > .assignment-author { color: #617487; font-size: 0.73rem; margin-top: 3px; }

@media (max-width: 420px) {
    .workforce-inspector__plan-summary-row { grid-template-columns: 85px minmax(0, 1fr); }
}

.workforce-inspector__plans .workforce-inspector__notice {
    margin-top: 0;
}

.workforce-inspector__termination {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid #efc56b;
    border-radius: 8px;
    background: #fff8e7;
    color: #75500b;
    font-size: 0.8rem;
}

.workforce-inspector__termination span,
.workforce-inspector__termination small {
    font-size: 0.74rem;
}

.workforce-inspector__plan-copy {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    padding: 10px 12px 8px;
}

.workforce-inspector__decision-text {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 12px;
    min-width: 0;
    overflow-wrap: anywhere;
}

.workforce-inspector__decision-text > span {
    flex: 0 0 auto;
    max-width: 100%;
}

.workforce-inspector__decision-text ::v-deep .employee-link {
    display: inline-block;
    max-width: 100%;
    vertical-align: baseline;
    overflow-wrap: anywhere;
}

.workforce-inspector__plan-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    padding: 7px 10px 9px 29px;
    border-top: 1px solid rgba(79, 128, 173, 0.18);
    background: rgba(255, 255, 255, 0.48);
}

.workforce-inspector__section {
    margin-top: 24px;
}

.workforce-inspector__section h3 {
    margin: 0 0 10px;
    color: #334455;
    font-size: 0.96rem;
}

.workforce-inspector__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
}

.workforce-inspector__section-header h3 {
    margin-bottom: 0;
}

.workforce-inspector__telegram-links {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-bottom: 12px;
}

.workforce-inspector__telegram-link {
    display: grid;
    grid-template-columns: 20px auto minmax(0, 1fr);
    align-items: center;
    gap: 7px;
    padding: 9px 10px;
    border: 1px solid #a8d5ef;
    border-radius: 8px;
    background: #eef8fe;
    color: #1876a8;
    font-size: 0.8rem;
}

.workforce-inspector__telegram-link:hover,
.workforce-inspector__telegram-link:focus {
    border-color: #54aee0;
    background: #e4f4fd;
    color: #0f638f;
    text-decoration: none;
}

.workforce-inspector__telegram-link strong {
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: right;
}

.workforce-inspector__characteristic-editor {
    margin-bottom: 12px;
    padding: 11px;
    border: 1px solid #d8e2ec;
    border-radius: 8px;
    background: #f8fafc;
}

.workforce-inspector__characteristic-editor .form-group {
    margin-bottom: 9px;
}

.workforce-inspector__characteristic-editor-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.workforce-inspector__list {
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.workforce-inspector__contact {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 9px;
    align-items: start;
    color: #3b4d5f;
    font-size: 0.82rem;
}

.workforce-inspector__contact > i {
    margin-top: 3px;
    color: #6686a6;
    text-align: center;
}

.workforce-inspector__contact a {
    overflow-wrap: anywhere;
}

.workforce-inspector__contact .workforce-inspector__phone {
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.45;
    font-variant-numeric: tabular-nums;
}

.workforce-inspector__item-label {
    color: #87929e;
    font-size: 0.7rem;
}

.workforce-inspector__characteristic {
    position: relative;
    padding: 10px 11px;
    border-left: 3px solid #8caccb;
    border-radius: 0 7px 7px 0;
    background: #f5f8fb;
}

.workforce-inspector__characteristic--editable {
    padding-right: 46px;
}

.workforce-inspector__characteristic--own {
    padding-right: 78px;
}

.workforce-inspector__characteristic--pinned {
    border-left-color: #d99a13;
    background: #fff9e8;
    box-shadow: inset 0 0 0 1px rgba(217, 154, 19, 0.18);
}

.workforce-inspector__characteristic-edit,
.workforce-inspector__characteristic-pin {
    position: absolute;
    top: 6px;
    right: 7px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border-radius: 50%;
    color: #7f8d9a;
    opacity: 0.72;
}

.workforce-inspector__characteristic-edit {
    right: 39px;
}

.workforce-inspector__characteristic-edit--new {
    animation: characteristic-pencil-wink .8s ease-in-out 3;
}

@keyframes characteristic-pencil-wink {
    50% { color: #1673c9; opacity: 1; background: rgba(22, 115, 201, .12); transform: scale(1.12); }
}

@media (prefers-reduced-motion: reduce) {
    .workforce-inspector__characteristic-edit--new { animation: none; }
}

.workforce-inspector__characteristic-edit:hover,
.workforce-inspector__characteristic-edit:focus,
.workforce-inspector__characteristic-pin:hover,
.workforce-inspector__characteristic-pin:focus {
    color: #2f679b;
    background: rgba(70, 125, 177, 0.1);
    text-decoration: none;
    opacity: 1;
}

.workforce-inspector__characteristic--pinned
.workforce-inspector__characteristic-pin {
    color: #a46f00;
    opacity: 1;
}

.workforce-inspector__characteristic-text {
    color: #354658;
    font-size: 0.82rem;
    line-height: 1.4;
}

.workforce-inspector__characteristic-source {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .35rem;
    margin: 0 2rem .45rem 0;
    color: #60758a;
    font-size: .75rem;
}

.workforce-inspector__characteristic-text p {
    margin: 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.workforce-inspector__characteristic-text p + p {
    margin-top: 0.7em;
}

.workforce-inspector__characteristic-meta {
    margin-top: 6px;
    color: #7c8997;
    font-size: 0.7rem;
}

.workforce-inspector__more {
    align-self: flex-start;
    padding: 5px 9px;
    border: 1px solid #b9ccdf;
    border-radius: 7px;
    background: #fff;
    color: #2e6da8;
    font-size: 0.76rem;
    font-weight: 700;
}

.workforce-inspector__more i {
    margin-right: 4px;
}

.workforce-inspector__more:hover:not(:disabled),
.workforce-inspector__more:focus:not(:disabled) {
    border-color: #7fa8cf;
    background: #edf6ff;
}

.workforce-inspector__more:disabled {
    cursor: wait;
    opacity: 0.7;
}

.workforce-inspector__load-error {
    color: #b23b3b;
    font-size: 0.74rem;
}

.workforce-inspector__empty {
    padding: 14px;
    border: 1px dashed #d6dde5;
    border-radius: 8px;
    color: #8894a0;
    font-size: 0.8rem;
    text-align: center;
}

.workforce-inspector__footer {
    position: relative;
    display: flex;
    z-index: 3;
    min-height: 62px;
    flex: 0 0 auto;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 14px 22px;
    border-top: 1px solid #e3e8ee;
    background: #fff;
    box-shadow: 0 -4px 12px rgba(31, 52, 73, 0.06);
}

@container workforce-inspector (max-width: 380px) {
    .workforce-inspector__skill-description { display: none; }
}

@media (max-width: 575.98px) {
    .workforce-inspector {
        top: 7vh;
        bottom: 0;
        width: 100%;
        height: auto;
        max-height: 93vh;
        max-height: 93dvh;
        border-radius: 14px 14px 0 0;
    }

    .workforce-inspector__header {
        padding: 18px 16px 14px;
    }

    .workforce-inspector__body {
        padding: 15px 16px 22px;
    }

    .workforce-inspector__footer {
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    }
}
</style>
